import { createContext, useCallback, useContext } from 'react'

import { useUser } from './userContext'

import { BuilderTier } from 'types/api.graphql'

type BuilderSubscriptionContextValue = {
  subscription: BuilderTier
  hasSubscription: (subscription: BuilderTier) => boolean
}

const BuilderSubscriptionContext =
  createContext<BuilderSubscriptionContextValue>(null)

export const BuilderSubscriptionProvider = ({ children }) => {
  const { builder } = useUser()

  const hasSubscription = useCallback(
    (subscription: BuilderTier) => {
      // Define base subscription tiers in hierarchical order (lowest to highest)
      const baseTiers = [
        BuilderTier.Free,
        BuilderTier.Lite,
        BuilderTier.Solo,
        BuilderTier.Starter,
        BuilderTier.Standard,
        BuilderTier.Pro,
        BuilderTier.Enterprise,
      ]

      // This mapping has been added to handle the new seat variants of plans
      // Existing uses of hasSubscription(legacyPlan) works for builders on new plans,
      // and vice versa, hasSubscription(newPlan) works with builders on legacy plans.

      // Map seat variants to base tier equivalents
      const seatToBaseTierMap = {
        [BuilderTier.LiteSeat]: BuilderTier.Lite,
        [BuilderTier.StandardSeat]: BuilderTier.Standard,
        [BuilderTier.ProSeat]: BuilderTier.Pro,
        [BuilderTier.EnterpriseSeat]: BuilderTier.Enterprise,
      }

      const getEffectiveTier = (tier: BuilderTier) => {
        return seatToBaseTierMap[tier] || tier
      }

      const builderEffectiveTier = getEffectiveTier(builder.subscription)
      const requiredEffectiveTier = getEffectiveTier(subscription)

      const builderTierIndex = baseTiers.indexOf(builderEffectiveTier)
      const requiredTierIndex = baseTiers.indexOf(requiredEffectiveTier)

      if (builderTierIndex === -1 || requiredTierIndex === -1) {
        return false
      }

      return builderTierIndex >= requiredTierIndex
    },
    [builder.subscription]
  )

  return (
    <BuilderSubscriptionContext.Provider
      value={{
        subscription: builder.subscription,
        hasSubscription,
      }}
    >
      {children}
    </BuilderSubscriptionContext.Provider>
  )
}

export const useBuilderSubscription = () =>
  useContext(BuilderSubscriptionContext)
